// Microsoft Clarity custom tags / events / identify.
//
// The tag itself is loaded by /clarity.js (see index.html). That snippet installs
// a queueing stub, so calls made before the real script lands are replayed. When
// Clarity is blocked (ad blockers, privacy extensions) window.clarity is simply
// missing, hence the guard on every call: analytics never breaks the app.
//
// Why bother: recordings are only useful if you can filter them. Tags make a
// session findable ("show me paid users who hit the editor on mobile"), events
// mark the moment something went wrong, and identify stitches a user's sessions
// together across visits.

/** Call the Clarity queue if the tag is present. Never throws. */
function clarity(...args) {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity(...args);
    }
  } catch { /* never break the app for analytics */ }
}

/**
 * Attach a filterable dimension to the session (and to the current page).
 * Values must be strings; Clarity keeps up to 128 chars each.
 */
export function clarityTag(key, value) {
  if (value === undefined || value === null || value === '') return;
  clarity('set', key, String(value));
}

/** Tag several dimensions at once: clarityTags({ plan: 'free', stage: 'editor' }). */
export function clarityTags(tags = {}) {
  for (const [key, value] of Object.entries(tags)) clarityTag(key, value);
}

/**
 * Mark a moment in the session. Shows up as a filter and on the recording
 * timeline. Clarity events carry no payload, so put the context in tags.
 */
export function clarityEvent(name) {
  if (!name) return;
  clarity('event', name);
}

/**
 * Stitch sessions belonging to the same user. We pass the Mongo user id, never
 * the email: Clarity hashes what it receives, but there is no reason to hand a
 * third party an address when an opaque id identifies the session just as well.
 */
export function clarityIdentify(userId) {
  if (!userId) return;
  clarity('identify', String(userId));
}

/**
 * Force this session to be recorded even when sampling would have dropped it.
 * Reserved for sessions we always want to be able to replay: payments, failed
 * exports, anything a user is likely to complain about.
 */
export function clarityUpgrade(reason) {
  if (!reason) return;
  clarity('upgrade', reason);
}

/** Where in the product the user currently is. One tag, low cardinality. */
export const STAGE = {
  landing: 'landing',
  auth: 'auth',
  dashboard: 'dashboard',
  upload: 'upload',
  transcribing: 'transcribing',
  editor: 'editor',
  export: 'export',
  billing: 'billing',
};

/** Set the funnel stage tag. Call on entering a stage, not on every render. */
export function setStage(stage) {
  clarityTag('stage', stage);
}

/**
 * Coarse buckets for a seconds balance. Exact numbers make terrible filters
 * (every session its own value); "none" / "under5" / "5to15" / "15to30" / "over30" do not.
 */
export function minutesBucket(seconds) {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return undefined;
  if (seconds <= 0) return 'none';
  if (seconds < 300) return 'under5';
  if (seconds <= 900) return '5to15';
  if (seconds <= 1800) return '15to30';
  return 'over30';
}
